import React, { useCallback, useState } from 'react';
import { Badge, Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import clsx from 'clsx';
import { DialogHeading } from 'components/dialog-title';
import { useTranslate } from 'hooks/use-translate';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import style from './index.module.scss';
import { FiltersSheetContext } from './context';

export { useIsInFiltersSheet, FILTERS_SHEET_PLACEHOLDER_KEY } from './context';

interface FiltersButtonProps {
  /** The filters that are not at their default; no badge at 0. */
  count: number;
  onClick: () => void;
  className?: string;
}

/**
 * N1 (brief pattern B): a list's filters below md -- a 40 px outlined icon
 * button whose badge counts the filters that are not at their default. It
 * opens the list's FiltersDialog; its label is its accessible name.
 */
export const FiltersButton: React.FC<FiltersButtonProps> = ({ count, onClick, className }) => {
  const { t } = useTranslate();
  return (
    <Badge
      badgeContent={count}
      color={'primary'}
      invisible={!count}
      className={clsx(style.badge, className)}
    >
      <Button
        variant={'outlined'}
        color={'primary'}
        className={style.button}
        onClick={onClick}
        aria-label={t('filters')}
      >
        <FilterListIcon />
      </Button>
    </Badge>
  );
};

interface FiltersDialogProps {
  open: boolean;
  title?: React.ReactNode;
  /** Discards the draft (the X, the backdrop, the back gesture). */
  onClose: () => void;
  /** Commits the draft; the caller closes the dialog. */
  onApply: () => void;
  /**
   * Puts the draft back to the list's defaults; the dialog stays open. N2c:
   * absent, the sheet has no "clear" (a page's own form without a reset).
   */
  onClear?: () => void;
  /** N2c: a page form's own label for its apply / reset; default "apply" / "clear". */
  applyLabel?: React.ReactNode;
  clearLabel?: React.ReactNode;
  /**
   * N2c: the fields stay mounted while the sheet is closed -- a page's own
   * form (PageFilters) keeps its one instance and its state.
   */
  keepMounted?: boolean;
}

/**
 * N1 (brief pattern B): a list's filters below md, in a bottom sheet -- the
 * fields stacked full-width, "clear" and "apply" pinned to its foot, the sheet
 * as tall as its fields (the theme's full-screen dialog, D-54, left most of the
 * screen blank under two or three fields). The fields edit a draft the caller
 * owns (useFiltersDialog for a useFilterForm screen; the data grid keeps its
 * own): "apply" commits it, "clear" resets it to the list's defaults, closing
 * discards it.
 */
export const FiltersDialog: React.FC<React.PropsWithChildren<FiltersDialogProps>> = ({
  open,
  title,
  onClose,
  onApply,
  onClear,
  applyLabel,
  clearLabel,
  keepMounted,
  children,
}) => {
  const { t } = useTranslate();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted={keepMounted}
      className={style.sheet}
      classes={{ paper: style.paper }}
    >
      <DialogHeading title={title ?? t('filters')} onClose={onClose} />
      <DialogContent className={style.content}>
        <FiltersSheetContext.Provider value={true}>{children}</FiltersSheetContext.Provider>
      </DialogContent>
      <DialogActions className={style.actions}>
        {onClear && (
          <Button variant={'outlined'} color={'primary'} onClick={onClear}>
            {clearLabel ?? t('clear')}
          </Button>
        )}
        <Button variant={'contained'} color={'primary'} onClick={onApply}>
          {applyLabel ?? t('apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface PageFiltersProps {
  /** The badge: how many of the form's values differ from the page's defaults. */
  count: number;
  /**
   * The form's own apply, when it has one: the sheet's primary action runs it
   * and closes. Absent, the form applies on change and the primary action only
   * closes the sheet.
   */
  onApply?: () => void;
  applyLabel?: React.ReactNode;
  /** The form's own reset, when it has one: the sheet's "clear". */
  onClear?: () => void;
  clearLabel?: React.ReactNode;
  /** A page without a grid bar: the button gets a bar row of its own. */
  bar?: boolean;
}

/**
 * N2c (T1, SC-N2b-07): a page's own filter form -- at md and up the form as
 * it is, in place; below md the same form (same instance, same state, same
 * handlers) inside the filters sheet, opened by a FiltersButton that takes the
 * grid bar's filters slot (just before the bar's actions, D-118) or, on a page
 * without a grid bar, a bar row of its own. The page's apply semantics stay:
 * a form that applies on change keeps doing so behind the sheet, the sheet's
 * primary action closes it; a form's own apply / reset are the sheet's footer.
 */
export const PageFilters: React.FC<React.PropsWithChildren<PageFiltersProps>> = ({
  count,
  onApply,
  applyLabel,
  onClear,
  clearLabel,
  bar,
  children,
}) => {
  const isBelowMd = useIsBelowMd();
  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const apply = useCallback(() => {
    if (onApply) {
      onApply();
    }
    setOpen(false);
  }, [onApply]);

  if (!isBelowMd) {
    return <>{children}</>;
  }

  const button = <FiltersButton count={count} onClick={open} className={style.pageButton} />;
  const sheet = (
    <FiltersDialog
      open={isOpen}
      onClose={close}
      onApply={apply}
      applyLabel={applyLabel}
      onClear={onClear}
      clearLabel={clearLabel}
      keepMounted
    >
      {children}
    </FiltersDialog>
  );
  return bar ? (
    <div className={style.pageBar}>
      {button}
      {sheet}
    </div>
  ) : (
    <>
      {button}
      {sheet}
    </>
  );
};
